import React, { useState } from 'react';
import { X, Activity, Flame, Lock, LogOut, Stethoscope, AlertCircle, Radio } from 'lucide-react';
import C from '../../constants/colors';

const EVENT_TYPES = [
  { type: 'DRILL',      label: 'Drill',      color: C.blue,   icon: Activity },
  { type: 'FIRE',       label: 'Fire',       color: C.orange, icon: Flame },
  { type: 'LOCKDOWN',   label: 'Lockdown',   color: C.purple, icon: Lock },
  { type: 'EVACUATION', label: 'Evacuation', color: C.red,    icon: LogOut },
  { type: 'MEDICAL',    label: 'Medical',    color: C.pink,   icon: Stethoscope },
  { type: 'OTHER',      label: 'Other',      color: C.amber,  icon: AlertCircle },
];

export default function EventModal({ companies, sites, zones, onStart, onClose }) {
  const [companyId, setCompanyId] = useState(companies[0]?.id || '');
  const [siteId, setSiteId] = useState('');
  const [eventType, setEventType] = useState('');
  const [scope, setScope] = useState('SITE');
  const [scopeZoneId, setScopeZoneId] = useState('');

  const filteredSites = sites.filter(s => s.companyId === companyId);
  const filteredZones = zones.filter(z => z.siteId === siteId);
  const canStart = companyId && siteId && eventType;

  function handleStart() {
    if (!canStart) return;
    onStart({ siteId, companyId, eventType, scope, scopeZoneId: scope === 'ZONE' ? scopeZoneId : null });
    onClose();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '32px', width: '500px', maxWidth: '95vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
          <div>
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary }}>Start Emergency Event</h3>
            <p style={{ fontSize: '13px', color: C.textMuted, marginTop: '4px' }}>All employees at the selected site will be reset to pending status</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}><X size={20} /></button>
        </div>

        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Company</label>
          <select value={companyId} onChange={e => { setCompanyId(e.target.value); setSiteId(''); }}
            style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Site</label>
          <select value={siteId} onChange={e => setSiteId(e.target.value)}
            style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
            <option value=''>Select a site...</option>
            {filteredSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Event Type</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px' }}>
            {EVENT_TYPES.map(({ type, label, color, icon: Icon }) => (
              <button key={type} onClick={() => setEventType(type)} style={{
                padding: '12px 8px', borderRadius: '10px', border: `2px solid ${eventType === type ? color : C.border}`,
                background: eventType === type ? color + '22' : C.surface, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                transition: 'all 0.15s',
              }}>
                <Icon size={18} color={eventType === type ? color : C.textSecondary} />
                <span style={{ fontSize: '12px', fontWeight: 600, color: eventType === type ? color : C.textSecondary }}>{label}</span>
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Scope</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {['SITE','ZONE'].map(s => (
              <button key={s} onClick={() => setScope(s)} style={{
                flex: 1, padding: '10px', borderRadius: '8px',
                border: `2px solid ${scope === s ? C.blue : C.border}`,
                background: scope === s ? C.blue + '22' : C.surface,
                color: scope === s ? C.blue : C.textSecondary,
                fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}>
                {s === 'SITE' ? 'Entire Site' : 'Specific Zone'}
              </button>
            ))}
          </div>
          {scope === 'ZONE' && siteId && (
            <select value={scopeZoneId} onChange={e => setScopeZoneId(e.target.value)} style={{ width: '100%', marginTop: '8px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
              <option value=''>Select zone...</option>
              {filteredZones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
          )}
        </div>

        <button onClick={handleStart} disabled={!canStart} style={{
          width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
          background: canStart ? C.red : C.surface, color: canStart ? '#fff' : C.textMuted,
          fontSize: '15px', fontWeight: 700, cursor: canStart ? 'pointer' : 'not-allowed',
          fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}>
          <Radio size={16} />
          Start Event
        </button>
      </div>
    </div>
  );
}
